/**
 * NoteLevelSection — PRP-NOTES-1. The account default for which of chat's suggested customer notes
 * print without review: None, Brief (the default: only unusual lines, such as a code or OSHA reason)
 * or Detailed. An estimate can override it on its own page. Notes the user adds always print; chat's
 * internal notes never print. Saves on change and reverts if the save fails.
 */
import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';

import { getScopeSettings, updateScopeSettings } from '../../lib/scopeSettingsApi';

interface FrameProps {
  title: string;
  sub?: string;
  children: ReactNode;
  footer?: ReactNode;
}

type Level = 'none' | 'brief' | 'detailed';

export const LEVEL_OPTIONS: { key: Level; label: string; hint: string }[] = [
  { key: 'none', label: 'None', hint: 'No chat note prints unless you add it on the estimate page.' },
  { key: 'brief', label: 'Brief (recommended)', hint: 'Only unusual lines print: a code or OSHA reason, or a condition the reader would not expect.' },
  { key: 'detailed', label: 'Detailed', hint: 'Every note chat suggests prints, except ones that read like working notes (prices, to-dos, names).' },
];

export default function NoteLevelSection({ frame: Frame }: { frame: ComponentType<FrameProps> }) {
  const [level, setLevel] = useState<Level>('brief');
  const [load, setLoad] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'bad'; msg: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getScopeSettings()
      .then((s) => {
        if (cancelled) return;
        setLevel(s?.note_level ?? 'brief');
        setLoad('ready');
      })
      .catch(() => {
        if (!cancelled) setLoad('failed');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onPick(next: Level) {
    const prev = level;
    setLevel(next);
    setBusy(true);
    setFlash(null);
    try {
      const out = await updateScopeSettings({ note_level: next });
      setLevel(out?.note_level ?? next);
      setFlash({ kind: 'ok', msg: 'Saved. Estimates without their own level use it at the next export.' });
    } catch {
      setLevel(prev);
      setFlash({ kind: 'bad', msg: 'Could not save. The setting is unchanged.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame title="Chat notes on the printed estimate">
      <fieldset className="m-0 p-0 border-0 space-y-2" data-testid="note-level-settings" disabled={load !== 'ready' || busy}>
        <legend className="text-[12px] text-[var(--tx-lo)] mb-1">
          Chat writes notes as suggestions. Internal notes never print. Pick which suggestions print without review.
        </legend>
        {LEVEL_OPTIONS.map((o) => (
          <label key={o.key} className="flex items-start gap-2 text-[13px] text-[var(--tx-hi)]">
            <input type="radio" name="note-level" value={o.key} checked={level === o.key}
              data-testid={`note-level-${o.key}`} onChange={() => onPick(o.key)} className="mt-[3px]" />
            <span>
              {o.label}
              <span className="block text-[11.5px] text-[var(--tx-faint)]">{o.hint}</span>
            </span>
          </label>
        ))}
        {load === 'failed' && (
          <p className="m-0 text-[12.5px] text-[var(--bad)]">Could not load this setting. Try again later.</p>
        )}
        {flash && (
          <p role="status" data-testid="note-level-flash"
            className={`m-0 text-[12.5px] ${flash.kind === 'ok' ? 'text-[var(--ok)]' : 'text-[var(--bad)]'}`}>
            {flash.msg}
          </p>
        )}
      </fieldset>
    </Frame>
  );
}
